// usePasskeys — thin wrapper over Amplify v6's WebAuthn (passkey) APIs.
//
// Passkeys let a signed-in user register this device's biometric authenticator
// (Face ID / Touch ID / Windows Hello / Android fingerprint) as a Cognito
// credential, then sign in with it next time instead of password + TOTP.
//
// Enrollment (`associateWebAuthnCredential`) REQUIRES an authenticated session —
// Cognito rejects it otherwise. `listWebAuthnCredentials` also needs auth, so
// `refresh()` swallows errors and reports an empty list when signed out.
import { useState, useEffect, useCallback } from 'react';
import {
  associateWebAuthnCredential,
  listWebAuthnCredentials,
  deleteWebAuthnCredential,
} from 'aws-amplify/auth';

// WebAuthn is only usable where the platform authenticator API exists.
const supported =
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

export function usePasskeys() {
  const [credentials, setCredentials] = useState([]);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const { credentials: list = [] } = await listWebAuthnCredentials();
      setCredentials(list);
    } catch (err) {
      // Not authenticated (or the pool has no WebAuthn) — treat as "none".
      console.warn('usePasskeys: could not list credentials:', err?.message);
      setCredentials([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Triggers the browser's Face ID / fingerprint prompt. Throws on cancel /
  // unsupported so callers can surface a friendly message.
  const enroll = useCallback(async () => {
    await associateWebAuthnCredential();
    await refresh();
  }, [refresh]);

  const remove = useCallback(
    async (credentialId) => {
      await deleteWebAuthnCredential({ credentialId });
      await refresh();
    },
    [refresh]
  );

  return { supported, credentials, refresh, enroll, remove };
}

export default usePasskeys;
